// LandingSalesResultScreen — a Landing Varázsló DEDIKÁLT
// eredménykomponense a regisztrációs oldal 8-szekciós Szöveg nézetéhez.
//
// KERET (a Webinár Varázsló eredményképernyőjének mintáját követve, azzal
// pixelpontosan egyező szerkezetben):
//   • Felső agentsáv: PostHeader (importálva a MagnetPostFacebook-ból)
//     Beállítás / Webinár kapcsolóval; „Webinár" aktív.
//   • 440px + 1fr grid layout.
//   • BAL: sötét finomítópanel, alul chat input.
//   • JOBB: fejléc a 3 művelet gombbal (Teljes szöveg másolása / Letöltés
//     / Mentés a Tudástárba), alatta krémszínű munkaterület + egyetlen
//     fehér dokumentumkártya (width 100% a jobb területen belül).
//
// Semmilyen módon nem importál a `WebinarSectionCarousel`-ból, csak a
// `PostHeader` közös komponenst használja fel (a MagnetPostFacebook.js-t
// NEM módosítjuk).
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy, DownloadSimple, BookmarkSimple, PencilSimple,
  ArrowsCounterClockwise, CircleNotch, Calendar, CheckCircle,
  PaperPlaneTilt, Paperclip, X as XIcon, Minus, ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PostHeader } from "./MagnetPostFacebook";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";

const GOLD_DEEP = "#8a6206";
const GOLD_LABEL = "#b5850a";
const GOLD_PRIMARY = "#eab308";
const GOLD_LIGHT_BG = "#fdf3d6";
const CREAM_WORK = "#f4ede2";
const CARD_BORDER = "#ece2c8";
const TEXT_DARK = "#221f19";
const TEXT_MID = "#4b4438";
const TEXT_BODY = "#3a3428";
const TEXT_SECOND = "#5c5648";
const TEXT_META = "#9b8f76";
const DIVIDER_START = "#f3d98a";
const DIVIDER_END = "#f9f2dd";

// A Webinár Varázsló CreamActionPill-jével PIXELPONTOSAN egyező arany
// pill háttér és keret. Ne módosítsd — a Webinár referenciájából származik.
const GOLD_BG = "rgba(255,219,92,0.14)";
const GOLD_BORDER = "rgba(255,219,92,0.36)";

// A Webinár Varázslóval IDENTIKUS 6-elemű quick-refine készlet.
const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb",     instruction: "Erősebb érzelmi feszültség, mélyebb fájdalom, konkrétabb jelenet, nagyobb hatás." },
  { id: "soft",     label: "Lágyabb",     instruction: "Melegebb, empatikusabb, barátságosabb, több megértés." },
  { id: "concrete", label: "Konkrétabb",  instruction: "Több konkrét részlet, időpont, hely, tárgy, érzés — semmi általánosítás." },
  { id: "story",    label: "Több sztori", instruction: "Cseréld ki vagy bővítsd ki egy rövid, életszagú személyes vagy hipotetikus jelenettel." },
  { id: "humor",    label: "Humorosabb",  instruction: "Adj bele finom öniróniát vagy könnyed humort, de maradj empatikus." },
  { id: "surprise", label: "Lepj meg!",   instruction: "Legyél kreatívabb, váratlanabb, erősebb hook vagy fordulat ebben a részben." },
];

/* ─── page ↔ text helpers ──────────────────────────────────────────
 * A `mod: "snippet"` esetnél a backend csak a kijelölt rész új
 * szöveges változatát adja vissza. Az `Alkalmazom` gomb az aktuális
 * `page` objektumban megkeresi a kijelölt idézetet bármelyik string
 * mezőben, és lecseréli az új snippetre. Az első előfordulást cseréli
 * — pontosan úgy, ahogy a WebinarSectionCarousel `applySnippetReplacement`
 * fallback-je a strukturált slide-fákon dolgozik.
 */
function _replaceInPageDeep(node, quote, snippet, state) {
  if (state.done) return node;
  if (typeof node === "string") {
    const idx = node.indexOf(quote);
    if (idx !== -1) {
      state.done = true;
      return node.slice(0, idx) + snippet + node.slice(idx + quote.length);
    }
    return node;
  }
  if (Array.isArray(node)) {
    return node.map((child) => _replaceInPageDeep(child, quote, snippet, state));
  }
  if (node && typeof node === "object") {
    const next = {};
    for (const k of Object.keys(node)) {
      next[k] = _replaceInPageDeep(node[k], quote, snippet, state);
    }
    return next;
  }
  return node;
}
function applyQuoteReplacement(page, quote, snippet) {
  const state = { done: false };
  const nextPage = _replaceInPageDeep(page, quote, snippet, state);
  return { page: nextPage, changed: state.done };
}

export default function LandingSalesResultScreen({
  inputs, chosenTitle, initialPage, onBack, onSavedToVault, onNavigateToThankyou,
}) {
  const [page, setPage] = useState(initialPage);
  const [savedName, setSavedName] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [proofreading, setProofreading] = useState(false);
  const [proofreadDone, setProofreadDone] = useState(false);
  // ── F3-mintás refine state machine (Webinár Varázslóval identikus) ──
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle"); // idle | loading | suggestion | applied
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selection, setSelection] = useState(null);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  // Sales oldalon nincs időpont-normalizálás — a Sales hero saját mezőket
  // használ (eyebrow, offer_name, title, subtitle, supporting_line, cta),
  // amelyeket a Writer és/vagy a user szerkeszt közvetlenül.
  const _normalizeInitialPage = (raw) => raw;

  useEffect(() => { setPage(_normalizeInitialPage(initialPage)); }, [initialPage]);

  // ── Szövegkijelölés figyelése a fehér dokumentumkártyán belül ──
  useEffect(() => {
    const handler = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const text = String(sel.toString() || "").trim();
        if (!text || text.length < 3) return;
        const anchor = sel.anchorNode?.parentElement?.closest?.('[data-testid="landing-sales-result-doc"]');
        const focus = sel.focusNode?.parentElement?.closest?.('[data-testid="landing-sales-result-doc"]');
        if (anchor && focus) setSelection(text);
      } catch { /* noop */ }
    };
    document.addEventListener("mouseup", handler);
    document.addEventListener("keyup", handler);
    return () => {
      document.removeEventListener("mouseup", handler);
      document.removeEventListener("keyup", handler);
    };
  }, []);

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const updateSection = (sectionKey, newValue) =>
    setPage((p) => ({ ...p, [sectionKey]: newValue }));

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const copyAll = async () => {
    const text = pageToPlainText(page);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Teljes szöveg vágólapra másolva");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Nem sikerült a másolás"); }
  };
  const downloadAll = () => {
    const text = pageToPlainText(page);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-oldal-${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Letöltés elindítva");
  };
  const saveToVault = async () => {
    if (saving) return;
    const nameSuggestion = (chosenTitle?.webinar_name || page?.hero?.title || "Regisztrációs oldal").slice(0, 80);
    const name = window.prompt("Milyen néven mentsük a Tudástárba?", nameSuggestion);
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post("/conversion/landing-sales/page/save", {
        name: name.trim(), page, chosen_title: chosenTitle || null, inputs: inputs || null,
      });
      if (res.data?.ok) {
        setSavedName(name.trim());
        toast.success("Mentve a Tudástárba");
        if (onSavedToVault) onSavedToVault();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen");
    } finally { setSaving(false); }
  };

  // ── Egyszeri magyar nyelvi tisztítás — a jelenlegi page-et küldi a
  // proofread endpointra, majd frissíti a state-et. A hero cím/alcím
  // szigorúan az eredetiből marad (backend garantálja).
  const runProofread = async () => {
    if (proofreading) return;
    const ok = window.confirm(
      "A rendszer egyszeri magyar nyelvi tisztítást végez az egész Sales oldalon. "
      + "A tartalom (ígéretek, árak, garancia, testimonialok, CTA-célok) NEM változik — "
      + "csak nyelvi hibák javulnak. Folytatod?"
    );
    if (!ok) return;
    setProofreading(true);
    setProofreadDone(false);
    try {
      const res = await api.post("/conversion/landing-sales/page/proofread", {
        current_page: page,
      });
      const newPage = res.data?.page;
      if (newPage && typeof newPage === "object" && newPage.hero) {
        setPage(newPage);
        setProofreadDone(true);
        toast.success("Nyelvi tisztítás kész — tartalom változatlan.");
        setTimeout(() => setProofreadDone(false), 4000);
      } else {
        toast.error("A nyelvi tisztítás nem adott vissza érvényes választ.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nyelvi tisztítás sikertelen");
    } finally {
      setProofreading(false);
    }
  };

  /* ─── F3-mintás refine — közös adapter (WebinarSectionCarousel-lel identikus flow) ─ */

  const _callRefine = async (utasitas, quoteVal) =>
    api.post("/conversion/landing-sales/page/refine", {
      chosen_title: chosenTitle || {},
      webinar_cim: inputs?.webinar_cim || "",
      mit_tanuljon: inputs?.mit_tanuljon || "",
      miert_te: inputs?.miert_te || "",
      hova_hivjuk: inputs?.hova_hivjuk || "",
      current_page: page,
      user_instruction: utasitas || "",
      quote: quoteVal || null,
    });

  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = ((instructionOverride && typeof instructionOverride === "string" ? instructionOverride : "") || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    // Ha quick-button jött, az utasítás a QUICK_REFINE_ACTIONS-ből
    const finalInstr = quickButtonId
      ? (QUICK_REFINE_ACTIONS.find((q) => q.id === quickButtonId)?.instruction || utasitas)
      : utasitas;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride && !quickButtonId) setChatInput("");
    try {
      const res = await _callRefine(finalInstr, quoteToSend);
      const data = res.data || {};
      setSuggestion({
        mod: data.mod === "snippet" ? "snippet" : "teljes",
        snippet: data.snippet || "",
        teljesPage: data.page || null,
        instr: finalInstr,
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch (e) {
      setSuggestion({ mod: "error", instr: finalInstr, quote: quoteToSend || null });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const previousSnippet = suggestion.mod === "snippet"
      ? (suggestion.snippet || "")
      : "";
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await _callRefine(
        (suggestion.instr || "")
          + "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt."
          + (previousSnippet ? ` Az előző javaslat ez volt: "${previousSnippet.slice(0, 280)}". Kerüld el az ismétlést.` : ""),
        suggestion.quote,
      );
      const data = res.data || {};
      setSuggestion((s) => ({
        ...s,
        snippet: data.snippet || "",
        teljesPage: data.page || null,
        mod: data.mod === "snippet" ? "snippet" : "teljes",
      }));
    } catch (e) {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const snapshot = JSON.parse(JSON.stringify(page));
    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      const { page: nextPage, changed } = applyQuoteReplacement(
        page, suggestion.quote, suggestion.snippet,
      );
      if (!changed) {
        toast.error("A kijelölt rész nem található már a szövegben.");
        return;
      }
      setUndoSnapshot(snapshot);
      setPage(nextPage);
    } else if (suggestion.mod === "teljes" && suggestion.teljesPage) {
      setUndoSnapshot(snapshot);
      // A teljes új page is átmegy a determinisztikus időpont-bekötésen
      // (a Writer által kitalált időpontot NEM használjuk).
      setPage(_normalizeInitialPage(suggestion.teljesPage));
    } else {
      return;
    }
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle");
      setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setPage(undoSnapshot);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  const eventType = (page?.hero?.event_type || "webinár").trim() || "webinár";

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", color: "#e8e8f0", overflow: "hidden" }}
      data-testid="landing-sales-result-shell"
    >
      {/* Felső agentsáv — PostHeader (Beállítás / Webinár kapcsoló) */}
      <PostHeader
        onBackToOpening={onBack}
        activeTab="tab2"
        onTabChange={(t) => { if (t === "tab1") onBack?.(); }}
        tab2Disabled={false}
        headerTitle="Landing Varázsló · Sales oldal"
        tab2Label="Sales oldal"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
        {/* ─── BAL — Finomító panel (WebinarSectionCarousel-lel pixelpontosan
             egyező elrendezés, állapotgép és „Javasolt változat" flow) ─── */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="landing-sales-result-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                  letterSpacing: "-0.015em", color: "#ffffff",
                  marginBottom: 18,
                }}
              >
                Elkészült a regisztrációs oldalad.
              </div>
              <div style={{
                fontSize: 16, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0",
              }}>
                Olvasd át a jobb oldalon a regisztrációs oldalt, szekcióról szekcióra.
                Ha valamit finomítanál, jelölj ki egy részt, és írd lent, mit
                változtassak. Ha minden rendben, mentsd el a Tudástárba.
              </div>
            </div>

            {/* Selection workspace */}
            <AnimatePresence>
              {(selection || refinePhase === "loading" || (refinePhase === "suggestion" && suggestion)) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="landing-sales-result-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div style={{
                      background: "rgba(255,255,255,0.04)",
                      borderLeft: "2px solid rgba(124,108,242,0.40)",
                      borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                    }}>
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{
                            fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                            color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                          }}>KIJELÖLT RÉSZ</div>
                          <div style={{
                            fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                            display: "-webkit-box", WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!(refinePhase === "suggestion" && suggestion) && refinePhase !== "loading" && (
                          <button
                            type="button"
                            onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                            data-testid="landing-sales-result-selection-clear"
                          >
                            <XIcon size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {refinePhase !== "loading" && !(refinePhase === "suggestion" && suggestion) && selection && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="landing-sales-result-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id}
                          type="button"
                          onClick={() => requestRefine(qa.instruction, qa.id)}
                          data-testid={`landing-sales-result-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap", textAlign: "center", width: "100%",
                          }}
                        >{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {refinePhase === "loading" && (
                    <div
                      className="flex items-center gap-2.5"
                      style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                      data-testid="landing-sales-result-refine-loading"
                    >
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {refinePhase === "suggestion" && suggestion && (
                    <div data-testid="landing-sales-result-suggestion">
                      <SuggestionCard
                        snippet={
                          suggestion.mod === "snippet"
                            ? (suggestion.snippet || "")
                            : (suggestion.teljesPage ? pageToPlainText(suggestion.teljesPage) : "")
                        }
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {refinePhase === "applied" && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="landing-sales-result-applied"
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const inputDisabled = refinePhase === "loading" || (refinePhase === "suggestion" && suggestion);
              if (chatInput.trim() && !inputDisabled) requestRefine();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="landing-sales-result-finomito-form"
          >
            {(() => {
              const isLoading = refinePhase === "loading";
              const showSuggestion = refinePhase === "suggestion" && suggestion;
              const inputDisabled = isLoading || showSuggestion;
              const placeholder = selection
                ? "Mit változtassak a kijelölt részen?"
                : "Jelölj ki egy részt jobboldalt, és írd ide, mit változtassak.";
              return (
                <>
                  <div style={{
                    background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                    border: "1px solid rgba(243,238,228,0.14)",
                    borderRadius: 12,
                    padding: "5px 5px 5px 14px",
                    display: "flex", alignItems: "center", gap: 10,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                    opacity: inputDisabled ? 0.5 : 1,
                    pointerEvents: inputDisabled ? "none" : "auto",
                  }}>
                    <button
                      type="button"
                      aria-label="Csatolás"
                      style={{
                        background: "transparent", border: "none", padding: 0,
                        color: "#8a8a9c", cursor: "pointer",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Paperclip size={14} weight="regular" />
                    </button>
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          if (chatInput.trim() && !inputDisabled) requestRefine();
                        }
                      }}
                      placeholder={placeholder}
                      data-testid="landing-sales-result-chat-input"
                      disabled={inputDisabled}
                      className="flex-1 outline-none"
                      style={{
                        background: "transparent", border: "none", color: "white",
                        fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                      }}
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || inputDisabled}
                      data-testid="landing-sales-result-chat-send"
                      aria-label="Küldés"
                      style={{
                        width: 36, height: 36,
                        background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                        color: "white", borderRadius: 9, border: "none",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                        opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                        boxShadow: chatInput.trim() && !inputDisabled
                          ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                          : "none",
                      }}
                    >
                      {isLoading
                        ? <CircleNotch size={15} weight="bold" className="animate-spin" />
                        : <PaperPlaneTilt size={15} weight="fill" />}
                    </button>
                  </div>
                  <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
                    <span>Tipp: jelölj ki egy részt jobboldalt — a kérésed arra fog vonatkozni.</span>
                    <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
                  </div>
                </>
              );
            })()}
          </form>
        </div>

        {/* ─── JOBB — Eredményterület (krém wrapper: 3 gomb + fehér doc) ── */}
        <div className="h-full overflow-y-auto min-w-0" style={{ background: "#0a0a12" }}>
          <div style={{ padding: "26px" }}>
            <div
              style={{ background: CREAM_WORK, borderRadius: 16, padding: "22px 26px 26px" }}
              data-testid="landing-sales-result-cream"
            >
              {/* Fejléc gombok — PIXELPONTOSAN a Webinár Varázsló
                  `CreamActionPill`-je (arany háttér, arany keret, 999
                  border-radius, 12/500 tipó, 6px gap). Ne módosítsd a
                  komponenst — a Webinár referenciából származik. */}
              <div
                className="flex justify-end items-center flex-wrap"
                style={{ gap: 6, marginBottom: 20 }}
                data-testid="landing-sales-result-actions-bar"
              >
                <CreamActionPill
                  onClick={copyAll}
                  icon={copied ? CheckCircle : Copy}
                  label={copied ? "Másolva" : "Másolás"}
                  testid="landing-sales-result-copy-all"
                  active={copied}
                />
                <CreamActionPill
                  onClick={runProofread}
                  icon={proofreading ? CircleNotch : (proofreadDone ? CheckCircle : Sparkle)}
                  label={proofreading ? "Tisztítás…" : proofreadDone ? "Tisztítva" : "Nyelvi tisztítás"}
                  testid="landing-sales-result-proofread"
                  disabled={proofreading}
                  active={proofreadDone}
                  spinning={proofreading}
                />
                <CreamActionPill
                  onClick={downloadAll}
                  icon={DownloadSimple}
                  label="Letöltés"
                  testid="landing-sales-result-download"
                />
                <CreamActionPill
                  onClick={saveToVault}
                  icon={savedName ? CheckCircle : BookmarkSimple}
                  label={saving ? "Mentés…" : savedName ? "Mentve" : "Mentés a Tudástárba"}
                  testid="landing-sales-result-save"
                  disabled={saving}
                  active={!!savedName}
                />
              </div>

              <motion.article
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}
                style={{
                  background: "#ffffff", borderRadius: 20,
                  border: `0.5px solid ${CARD_BORDER}`,
                  boxShadow: "0 1px 3px rgba(60,50,20,0.05)",
                  padding: "56px 64px", width: "100%",
                }}
                className="landing-sales-result-doc"
                data-testid="landing-sales-result-doc"
              >
                <SalesHeroSection value={page.hero}
                  onChange={(v) => updateSection("hero", v)} />

                <Divider />
                <SalesTextSection sectionKey="identification"
                  value={page.identification}
                  onChange={(v) => updateSection("identification", v)} />

                <Divider />
                <SalesTextSection sectionKey="beliefs"
                  value={page.beliefs}
                  onChange={(v) => updateSection("beliefs", v)} />

                <Divider />
                <SalesMechanismSection value={page.mechanism}
                  onChange={(v) => updateSection("mechanism", v)} />

                {page.early_proof && (
                  <>
                    <Divider />
                    <SalesProofSection sectionKey="early_proof"
                      value={page.early_proof}
                      onChange={(v) => updateSection("early_proof", v)} />
                  </>
                )}

                <Divider />
                <SalesOfferSection value={page.offer}
                  onChange={(v) => updateSection("offer", v)} />

                <Divider />
                <SalesFitSection value={page.fit}
                  onChange={(v) => updateSection("fit", v)} />

                {page.deep_proof && (
                  <>
                    <Divider />
                    <SalesProofSection sectionKey="deep_proof"
                      value={page.deep_proof}
                      onChange={(v) => updateSection("deep_proof", v)} />
                  </>
                )}

                {page.guarantee && (
                  <>
                    <Divider />
                    <SalesGuaranteeSection value={page.guarantee}
                      onChange={(v) => updateSection("guarantee", v)} />
                  </>
                )}

                <Divider />
                <SalesTextSection sectionKey="decision"
                  value={page.decision}
                  onChange={(v) => updateSection("decision", v)} />

                <Divider />
                <SalesPriceCtaSection value={page.price_cta}
                  onChange={(v) => updateSection("price_cta", v)} />

                <Divider />
                <SalesFaqSection value={page.faq}
                  onChange={(v) => updateSection("faq", v)} />

                <Divider />
                <SalesClosingCtaSection value={page.closing_cta}
                  onChange={(v) => updateSection("closing_cta", v)} />

                <Divider />
                <SalesPsSection value={page.ps}
                  onChange={(v) => updateSection("ps", v)} />
              </motion.article>

              {/* ── Kereszt-agent továbbvezető gomb ─────────────────
                  Pixelpontosan a MagnetPost `post-fb-spinoff-*` pill
                  mintáját követi (fontSize 12.5 / padding 8×14 /
                  borderRadius 999 / 1px border), Landing arany
                  akcenttel. A gomb NEM része a landing copy-nak
                  (a dokumentumkártyán KÍVÜL, a krém wrapperen belül,
                  jobbra rendezve, levegős térközzel). */}
              {onNavigateToThankyou && (
                <div
                  className="flex justify-end"
                  style={{ marginTop: 28, marginBottom: 4 }}
                  data-testid="landing-sales-result-next-thankyou-wrap"
                  data-html2canvas-ignore="true"
                >
                  <button
                    type="button"
                    onClick={onNavigateToThankyou}
                    data-testid="landing-sales-result-next-thankyou-btn"
                    style={{
                      fontSize: 12.5, padding: "8px 14px", borderRadius: 999,
                      background: GOLD_BG, border: `1px solid ${GOLD_BORDER}`,
                      color: "#8a6d00", cursor: "pointer",
                      fontFamily: "inherit", fontWeight: 500,
                      display: "inline-flex", alignItems: "center", gap: 6,
                      transition: "all 180ms ease",
                    }}
                    title="Ugrás a Landing kártyákhoz — Köszönőoldal auto-kijelölve, a lila gomb aktív"
                  >
                    Tovább a Köszönőoldalhoz →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── CreamActionPill — pixelpontosan a Webinár Varázslóból
     (`WebinarSectionCarousel.js` 488. sor). Ugyanaz a magasság, padding,
     border-radius, keret, betűméret és font-weight; disabled/active
     állapotok is identikusak. */
function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled, spinning }) {
  const bg = active ? "rgba(29,158,117,0.10)" : GOLD_BG;
  const border = active ? "rgba(29,158,117,0.30)" : GOLD_BORDER;
  const color = active ? "#1d9e75" : "#8a6d00";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: bg, border: `1px solid ${border}`,
        borderRadius: 999, color,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight={spinning ? "bold" : "regular"} className={spinning ? "animate-spin" : undefined} /> {label}
    </button>
  );
}

/* ───────── Divider ───────── */
function Divider() {
  return (
    <div style={{ height: 1, background: `linear-gradient(90deg, ${DIVIDER_START}, ${DIVIDER_END})`, margin: "44px 0 38px 0" }} aria-hidden="true" />
  );
}

/* ───────── Section actions (Copy / Edit / Regenerate) ───────── */
function SectionActions({ onCopy, onEdit, editActive, onRegenerate, regenerating, testidPrefix, showRegen = false }) {
  return (
    <div className="flex items-center gap-3" style={{ marginTop: 2 }}>
      <IconAction onClick={onCopy} icon={<Copy size={14} weight="regular" />} title="Másolás" testid={`${testidPrefix}-copy`} />
      <IconAction onClick={onEdit} icon={<PencilSimple size={14} weight={editActive ? "fill" : "regular"} />} title="Szerkesztés" testid={`${testidPrefix}-edit`} active={editActive} />
      {showRegen && (
        <IconAction
          onClick={onRegenerate} disabled={regenerating}
          icon={regenerating ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <ArrowsCounterClockwise size={14} weight="regular" />}
          title="Újragenerálás" testid={`${testidPrefix}-regen`}
        />
      )}
    </div>
  );
}
function IconAction({ onClick, icon, title, testid, active, disabled }) {
  return (
    <button
      type="button" onClick={onClick} title={title} disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center transition"
      style={{
        background: "transparent", border: "none", padding: 4,
        color: active ? GOLD_DEEP : "#e2cf87",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.color = GOLD_DEEP; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.color = active ? GOLD_DEEP : "#e2cf87"; }}
    >
      {icon}
    </button>
  );
}

/* ───────── HERO ───────── */
function HeroSection({ hero, eventType }) {
  const wn = (hero?.webinar_name || "").trim();
  const dt = (hero?.datetime || "").trim();
  return (
    <section data-testid="landing-sales-result-hero">
      <span style={{
        display: "inline-block", padding: "5px 12px", borderRadius: 999,
        background: GOLD_LIGHT_BG, color: GOLD_LABEL,
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", marginBottom: 20,
      }}>
        {(hero?.eyebrow || `INGYENES ÉLŐ ${eventType.toUpperCase()}`).trim()}
      </span>
      {/* Eseménynév + időpont egy közös metasorban (bal / jobb) */}
      <div className="flex items-baseline flex-wrap justify-between gap-x-6 gap-y-2" style={{ marginBottom: 14 }}>
        {wn ? (
          <div className="font-heading" data-testid="landing-sales-result-hero-name"
            style={{ fontSize: 22, color: GOLD_DEEP, fontWeight: 500, lineHeight: 1.35, letterSpacing: "-0.005em" }}>
            {wn}
          </div>
        ) : <span />}
        <div className="inline-flex items-center gap-1.5" data-testid="landing-sales-result-hero-datetime"
          style={{ fontSize: 13, color: "#8a7f68", fontFamily: "Inter, system-ui, sans-serif" }}>
          <Calendar size={15} color={GOLD_LABEL} weight="regular" />
          {dt || <em style={{ color: TEXT_META, fontStyle: "italic" }}>Add meg az esemény időpontját</em>}
        </div>
      </div>
      <h1 className="font-heading" data-testid="landing-sales-result-hero-title"
        style={{ fontSize: 31, color: TEXT_DARK, fontWeight: 500, lineHeight: 1.32, marginBottom: 18, letterSpacing: "-0.015em" }}>
        {hero?.title || ""}
      </h1>
      <p style={{ fontSize: 16, color: TEXT_SECOND, lineHeight: 1.7, marginBottom: 24 }} data-testid="landing-sales-result-hero-subtitle">
        {hero?.subtitle || ""}
      </p>
      <button type="button" data-testid="landing-sales-result-hero-cta"
        style={{
          background: "linear-gradient(180deg, #ffdb5c, #eab308)", color: "#4a3400",
          border: "none", borderRadius: 999, padding: "14px 28px",
          fontSize: 14, fontWeight: 600, cursor: "pointer",
          fontFamily: "Inter, system-ui, sans-serif",
          boxShadow: "0 4px 12px rgba(234,179,8,0.25)",
        }}>
        {hero?.cta || "Kérem az ingyenes helyem"}
      </button>
      {(hero?.microcopy || "").trim() && (
        <p style={{ fontSize: 12, color: TEXT_META, marginTop: 10 }} data-testid="landing-sales-result-hero-microcopy">
          {hero.microcopy}
        </p>
      )}
    </section>
  );
}

/* ───────── TEACHINGS ───────── */
function TeachingsSection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const items = value?.items || [];
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${items.map((it) => `${it.number}. ${it.text}`).join("\n\n")}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-sales/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-sales-result-teachings">
      <SectionHeader title={value?.title || "Ezt a három dolgot viszed magaddal"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-sales-result-teachings" />
      </SectionHeader>
      <div style={{ marginTop: 22 }}>
        {items.map((it, idx) => (
          <div key={idx} className="flex items-start" style={{ gap: 14, marginBottom: 18 }}>
            <div style={{
              width: 26, height: 26, flexShrink: 0, borderRadius: "50%",
              background: "linear-gradient(180deg, #ffdb5c, #eab308)",
              color: "#ffffff", fontFamily: "Satoshi, system-ui, sans-serif",
              fontSize: 14, fontWeight: 500,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 5px rgba(234,179,8,0.25)",
            }}>{it.number}</div>
            <EditableInline value={it.text} editing={editing}
              onChange={(newText) => onChange({ ...value, items: items.map((x, i) => i === idx ? { ...x, text: newText } : x) })}
              style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY }} multiline
              testid={`landing-sales-result-teachings-item-${idx}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────── EMPATHY ───────── */
function EmpathySection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const body = value?.body || [];
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${body.join("\n\n")}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-sales/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-sales-result-empathy">
      <SectionHeader title={value?.title || "Lehet, hogy most te is itt tartasz…"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-sales-result-empathy" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        {body.map((p, i) => (
          <EditableInline key={i} value={p} editing={editing}
            onChange={(nv) => onChange({ ...value, body: body.map((x, j) => j === i ? nv : x) })}
            style={{ fontSize: 15, lineHeight: 1.75, color: TEXT_BODY, marginBottom: 14 }} multiline
            testid={`landing-sales-result-empathy-p-${i}`} />
        ))}
      </div>
    </section>
  );
}

/* ───────── FOR YOU ───────── */
function ForYouSection({ value, eventType, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const items = value?.items || [];
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${items.map((it) => `- ${it}`).join("\n")}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-sales/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-sales-result-foryou">
      <SectionHeader title={value?.title || `Neked szól ez a ${eventType}, ha…`}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-sales-result-foryou" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        {items.map((it, i) => (
          <div key={i} className="flex items-start" style={{ gap: 10, marginBottom: 10 }}>
            <CheckCircle size={18} weight="fill" color={GOLD_PRIMARY} style={{ flexShrink: 0, marginTop: 2 }} />
            <EditableInline value={it} editing={editing}
              onChange={(nv) => onChange({ ...value, items: items.map((x, j) => j === i ? nv : x) })}
              style={{ fontSize: 15, lineHeight: 1.65, color: TEXT_BODY }}
              testid={`landing-sales-result-foryou-item-${i}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────── DIFFERENCE ───────── */
function DifferenceSection({ value, eventType, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${value?.highlight || ""}\n\n${value?.body || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-sales/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-sales-result-diff">
      <SectionHeader title={value?.title || `Miért más ez a ${eventType}?`}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-sales-result-diff" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        <EditableInline value={value?.highlight || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, highlight: nv })}
          style={{ fontFamily: "Satoshi, system-ui, sans-serif", fontSize: 16, fontWeight: 500, color: GOLD_DEEP, marginBottom: 10, lineHeight: 1.5 }}
          multiline testid="landing-sales-result-diff-highlight" />
        <EditableInline value={value?.body || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, body: nv })}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY }} multiline
          testid="landing-sales-result-diff-body" />
      </div>
    </section>
  );
}

/* ───────── SPEAKER ───────── */
function SpeakerSection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${value?.name ? value.name + "\n\n" : ""}${value?.bio || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-sales/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-sales-result-speaker">
      <SectionHeader title={value?.title || "Néhány szó rólam"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-sales-result-speaker" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        {(value?.name || editing) && (
          <EditableInline value={value?.name || ""} editing={editing}
            onChange={(nv) => onChange({ ...value, name: nv })}
            style={{ fontFamily: "Satoshi, system-ui, sans-serif", fontSize: 17, fontWeight: 500, color: TEXT_DARK, marginBottom: 10 }}
            testid="landing-sales-result-speaker-name" />
        )}
        <EditableInline value={value?.bio || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, bio: nv })}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY }} multiline
          testid="landing-sales-result-speaker-bio" />
      </div>
    </section>
  );
}

/* ───────── EVENT ───────── */
function EventSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\nDátum: ${value?.date || ""}\nIdőpont: ${value?.time || ""}\nForma: ${value?.format || ""}\nRészvétel: ${value?.price || ""}\n\n${value?.cta || ""}\n${value?.microcopy || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  // Ha `date` szabad szöveg (nem tartalmaz csak számokat / dátumszavakat)
  // és a `time` üres, VAGY ha `date` értéke ténylegesen kombinált szöveg,
  // akkor összevont „Dátum és időpont" mezőt mutatunk.
  const dateVal = (value?.date || "").trim();
  const timeVal = (value?.time || "").trim();
  const useCombined = dateVal && !timeVal;
  return (
    <section data-testid="landing-sales-result-event">
      <SectionHeader title={value?.title || "Időpont és részvétel"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          testidPrefix="landing-sales-result-event" showRegen={false} />
      </SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ gap: 22, marginTop: 22 }}>
        {useCombined ? (
          <EventField
            label="Dátum és időpont" value={dateVal} editing={editing}
            onChange={(v) => onChange({ ...value, date: v })}
            placeholder="Add meg az esemény időpontját"
            testid="landing-sales-result-event-date"
            spanTwo
          />
        ) : (
          <>
            <EventField label="Dátum" value={dateVal} editing={editing}
              onChange={(v) => onChange({ ...value, date: v })}
              placeholder="Add meg a dátumot" testid="landing-sales-result-event-date" />
            <EventField label="Időpont" value={timeVal} editing={editing}
              onChange={(v) => onChange({ ...value, time: v })}
              placeholder="Add meg az időpontot" testid="landing-sales-result-event-time" />
          </>
        )}
        <EventField label="Forma" value={value?.format || ""} editing={editing} onChange={(v) => onChange({ ...value, format: v })} placeholder="Online" testid="landing-sales-result-event-format" />
        <EventField label="Részvétel" value={value?.price || ""} editing={editing} onChange={(v) => onChange({ ...value, price: v })} placeholder="Ingyenes" testid="landing-sales-result-event-price" />
      </div>
      {(value?.microcopy || "").trim() && (
        <p style={{ fontSize: 12, color: TEXT_META, marginTop: 16 }}>{value.microcopy}</p>
      )}
    </section>
  );
}
function EventField({ label, value, editing, onChange, placeholder, testid, spanTwo }) {
  return (
    <div style={spanTwo ? { gridColumn: "span 2" } : undefined}>
      <div style={{ fontFamily: "Inter, system-ui, sans-serif", fontSize: 12, fontWeight: 600, color: GOLD_LABEL, marginBottom: 6, letterSpacing: "0.02em" }}>{label}</div>
      {editing ? (
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          style={{ width: "100%", background: "transparent", border: `1px dashed ${DIVIDER_START}`, borderRadius: 6, padding: "6px 8px", fontSize: 14, color: TEXT_BODY, fontFamily: "inherit" }} />
      ) : (
        <div style={{ fontSize: 14, color: value ? TEXT_BODY : TEXT_META, fontStyle: value ? "normal" : "italic" }} data-testid={testid}>
          {value || placeholder}
        </div>
      )}
    </div>
  );
}

/* ───────── CLOSING ───────── */
function ClosingSection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${value?.body || ""}\n\n${value?.cta || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-sales/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-sales-result-closing">
      <SectionHeader title={value?.title || ""}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-sales-result-closing" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        <EditableInline value={value?.body || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, body: nv })}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY, marginBottom: 20 }} multiline
          testid="landing-sales-result-closing-body" />
        <button type="button" data-testid="landing-sales-result-closing-cta"
          style={{
            background: "linear-gradient(180deg, #ffdb5c, #eab308)", color: "#4a3400",
            border: "none", borderRadius: 999, padding: "14px 28px",
            fontSize: 14, fontWeight: 600, cursor: "pointer",
            fontFamily: "Inter, system-ui, sans-serif",
            boxShadow: "0 4px 12px rgba(234,179,8,0.25)",
          }}>
          {value?.cta || "Kérem az ingyenes helyem"}
        </button>
      </div>
    </section>
  );
}

/* ───────── Section header ───────── */
/* ═══════════════════════════════════════════════════════════════════
   SALES OLDAL SZEKCIÓK — 14 blokk, ugyanazzal a stílussal, mint a
   Regisztrációs SectionHeader/EditableInline/SectionActions.
   ═══════════════════════════════════════════════════════════════════ */

function SalesHeroSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const patch = (k, val) => onChange({ ...v, [k]: val });
  const copySection = async () => {
    const lines = [v.eyebrow, v.offer_name, v.title, v.subtitle, v.supporting_line, v.cta].filter(Boolean);
    try { await navigator.clipboard.writeText(lines.join("\n")); toast.success("Hero másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div style={{ flex: 1, minWidth: 0 }}>
          {(v.eyebrow || editing) && (
            <div style={{
              display: "inline-block", fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase",
              color: "#8a6d00", fontWeight: 600,
              background: "rgba(255,219,92,0.14)", border: "1px solid rgba(255,219,92,0.36)",
              borderRadius: 999, padding: "4px 12px", marginBottom: 10,
            }}>
              <EditableInline value={v.eyebrow || ""} editing={editing}
                onChange={(nv) => patch("eyebrow", nv)}
                testid="landing-sales-result-hero-eyebrow" style={{ display: "inline" }} />
            </div>
          )}
          {(v.offer_name || editing) && (
            <div style={{ fontSize: 13, color: "#7a7060", marginBottom: 8 }}>
              <EditableInline value={v.offer_name || ""} editing={editing}
                onChange={(nv) => patch("offer_name", nv)}
                testid="landing-sales-result-hero-offer-name" />
            </div>
          )}
          <EditableInline value={v.title} editing={editing}
            onChange={(nv) => patch("title", nv)}
            testid="landing-sales-result-hero-title"
            className="font-heading"
            style={{ fontSize: 31, color: TEXT_DARK, fontWeight: 500, lineHeight: 1.32, letterSpacing: "-0.015em", marginBottom: 18 }} />
          <EditableInline value={v.subtitle} editing={editing} multiline
            onChange={(nv) => patch("subtitle", nv)}
            testid="landing-sales-result-hero-subtitle"
            style={{ fontSize: 16, lineHeight: 1.7, color: TEXT_SECOND, marginBottom: 20 }} />
          {(v.supporting_line || editing) && (
            <EditableInline value={v.supporting_line || ""} editing={editing} multiline
              onChange={(nv) => patch("supporting_line", nv)}
              testid="landing-sales-result-hero-supporting"
              style={{ fontSize: 15, lineHeight: 1.65, color: TEXT_MID, marginBottom: 18 }} />
          )}
          {(v.cta || editing) && (
            <div style={{
              display: "inline-flex", padding: "14px 28px", borderRadius: 999,
              background: "linear-gradient(180deg, #ffdb5c, #eab308)",
              color: "#4a3400", fontSize: 14, fontWeight: 600,
              fontFamily: "Inter, system-ui, sans-serif",
              boxShadow: "0 4px 12px rgba(234,179,8,0.25)",
            }}>
              <EditableInline value={v.cta || ""} editing={editing}
                onChange={(nv) => patch("cta", nv)}
                testid="landing-sales-result-hero-cta" style={{ color: "#4a3400" }} />
            </div>
          )}
        </div>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </div>
    </section>
  );
}

/* Általános szöveges szekció: title + body (string[]). Használva:
   identification, beliefs, decision. */
function SalesTextSection({ sectionKey, value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || { title: "", body: [] };
  const body = Array.isArray(v.body) ? v.body : [];
  const patchTitle = (t) => onChange({ ...v, title: t });
  const patchBody = (idx, bv) => {
    const next = body.slice(); next[idx] = bv;
    onChange({ ...v, body: next });
  };
  const copySection = async () => {
    try { await navigator.clipboard.writeText([v.title, ...body].filter(Boolean).join("\n\n")); toast.success("Szekció másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}
      data-testid={`landing-sales-result-${sectionKey}`}>
      <SectionHeader title={v.title || ""}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      {body.map((p, i) => (
        <EditableInline key={i} value={p} editing={editing} multiline
          onChange={(nv) => patchBody(i, nv)}
          testid={`landing-sales-result-${sectionKey}-body-${i}`}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_MID }} />
      ))}
      {editing && (
        <EditableInline value="" editing={true} multiline
          onChange={(nv) => onChange({ ...v, body: [...body, nv] })}
          testid={`landing-sales-result-${sectionKey}-body-new`}
          style={{ fontSize: 15, lineHeight: 1.7, color: "#9b9080", fontStyle: "italic" }} />
      )}
    </section>
  );
}

function SalesMechanismSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const body = Array.isArray(v.body) ? v.body : [];
  const copySection = async () => {
    try { await navigator.clipboard.writeText([v.title, v.highlight, ...body].filter(Boolean).join("\n\n")); toast.success("Mechanizmus másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionHeader title={v.title || ""}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      {v.highlight && (
        <div style={{
          padding: "12px 16px", borderLeft: "3px solid rgba(255,219,92,0.5)",
          background: "rgba(255,219,92,0.06)", borderRadius: 4,
          fontSize: 16, fontWeight: 500, color: TEXT_DARK, lineHeight: 1.5,
        }}>
          <EditableInline value={v.highlight} editing={editing} multiline
            onChange={(nv) => onChange({ ...v, highlight: nv })}
            testid="landing-sales-result-mechanism-highlight" />
        </div>
      )}
      {body.map((p, i) => (
        <EditableInline key={i} value={p} editing={editing} multiline
          onChange={(nv) => { const nb = body.slice(); nb[i] = nv; onChange({ ...v, body: nb }); }}
          testid={`landing-sales-result-mechanism-body-${i}`}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_MID }} />
      ))}
    </section>
  );
}

function SalesProofSection({ sectionKey, value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const items = Array.isArray(v.items) ? v.items : [];
  const copySection = async () => {
    const lines = [v.title];
    items.forEach((it) => {
      lines.push(`„${it.quote || ""}"`);
      const meta = [it.name, it.context, it.result].filter(Boolean).join(" · ");
      if (meta) lines.push(meta);
      lines.push("");
    });
    try { await navigator.clipboard.writeText(lines.filter(Boolean).join("\n")); toast.success("Bizonyíték másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 16 }}
      data-testid={`landing-sales-result-${sectionKey}`}>
      <SectionHeader title={v.title || ""}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      {items.map((it, idx) => (
        <div key={idx} style={{
          padding: "16px 20px",
          background: "rgba(0,0,0,0.02)", borderRadius: 8,
          borderLeft: "3px solid rgba(255,219,92,0.5)",
        }}>
          <div style={{ fontSize: 15.5, lineHeight: 1.65, color: TEXT_DARK, marginBottom: 8, fontStyle: "italic" }}>
            <EditableInline value={it.quote || ""} editing={editing} multiline
              onChange={(nv) => { const nb = items.slice(); nb[idx] = { ...it, quote: nv }; onChange({ ...v, items: nb }); }}
              testid={`landing-sales-result-${sectionKey}-quote-${idx}`} />
          </div>
          {(it.name || it.context || it.result) && (
            <div style={{ fontSize: 13, color: "#7a7060" }}>
              {[it.name, it.context, it.result].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function SalesOfferSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const comps = Array.isArray(v.components) ? v.components : [];
  const payment = Array.isArray(v.payment_options) ? v.payment_options : [];
  const copySection = async () => {
    const lines = [v.title, v.intro, v.offer_name, ""];
    comps.forEach((c) => {
      lines.push(`• ${c.name || ""}`);
      if (c.what_it_is) lines.push(`  ${c.what_it_is}`);
      if (c.outcome)    lines.push(`  → ${c.outcome}`);
      lines.push("");
    });
    if (v.total_value) lines.push(`Összérték: ${v.total_value}`);
    if (v.price)       lines.push(`Ár: ${v.price}`);
    if (payment.length) lines.push(`Fizetés: ${payment.join(" · ")}`);
    try { await navigator.clipboard.writeText(lines.filter((l) => l !== undefined).join("\n")); toast.success("Ajánlat másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionHeader title={v.title || ""}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      {v.intro && (
        <EditableInline value={v.intro} editing={editing} multiline
          onChange={(nv) => onChange({ ...v, intro: nv })}
          testid="landing-sales-result-offer-intro"
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_MID }} />
      )}
      {v.offer_name && (
        <div style={{ fontSize: 20, fontWeight: 600, color: TEXT_DARK }}>
          <EditableInline value={v.offer_name} editing={editing}
            onChange={(nv) => onChange({ ...v, offer_name: nv })}
            testid="landing-sales-result-offer-name" />
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {comps.map((c, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{
              flexShrink: 0, width: 8, height: 8, marginTop: 9, borderRadius: 999,
              background: "rgba(255,219,92,0.6)",
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="flex items-start justify-between flex-wrap" style={{ gap: 10, marginBottom: 4 }}>
                <EditableInline value={c.name || ""} editing={editing}
                  onChange={(nv) => { const nb = comps.slice(); nb[i] = { ...c, name: nv }; onChange({ ...v, components: nb }); }}
                  testid={`landing-sales-result-offer-comp-${i}-name`}
                  style={{ fontSize: 16, fontWeight: 600, color: TEXT_DARK, flex: 1, minWidth: 0 }} />
                {c.value && (
                  <span
                    data-testid={`landing-sales-result-offer-comp-${i}-value`}
                    style={{
                      flexShrink: 0, display: "inline-flex", alignItems: "center",
                      padding: "3px 10px", borderRadius: 999,
                      background: "rgba(255,219,92,0.14)", border: "1px solid rgba(255,219,92,0.36)",
                      color: GOLD_DEEP, fontSize: 12, fontWeight: 600,
                      whiteSpace: "nowrap", fontFamily: "Inter, system-ui, sans-serif",
                    }}
                  >
                    Érték: {c.value}
                  </span>
                )}
              </div>
              <EditableInline value={c.what_it_is || ""} editing={editing} multiline
                onChange={(nv) => { const nb = comps.slice(); nb[i] = { ...c, what_it_is: nv }; onChange({ ...v, components: nb }); }}
                testid={`landing-sales-result-offer-comp-${i}-what`}
                style={{ fontSize: 14.5, lineHeight: 1.6, color: TEXT_MID, marginBottom: 4 }} />
              {c.outcome && (
                <EditableInline value={c.outcome} editing={editing} multiline
                  onChange={(nv) => { const nb = comps.slice(); nb[i] = { ...c, outcome: nv }; onChange({ ...v, components: nb }); }}
                  testid={`landing-sales-result-offer-comp-${i}-outcome`}
                  style={{ fontSize: 14, lineHeight: 1.55, color: "#6a5f4c", fontStyle: "italic" }} />
              )}
            </div>
          </div>
        ))}
      </div>
      {v.total_value && (
        <div
          data-testid="landing-sales-result-offer-total-value"
          style={{
            marginTop: 4,
            padding: "12px 16px", borderRadius: 10,
            background: "rgba(255,219,92,0.10)", border: "1px solid rgba(255,219,92,0.32)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: GOLD_LABEL }}>
            Teljes érték
          </span>
          <span style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK, fontFamily: "Satoshi, system-ui, sans-serif" }}>
            {v.total_value}
          </span>
        </div>
      )}
      {(v.price || (payment.length > 0)) && (
        <SalesPricingCards
          price={v.price}
          paymentOptions={payment}
          totalValue={v.total_value}
        />
      )}
      {(v.price || payment.length > 0) && !hasStructuredPricing(v.price, payment) && (
        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 6 }}>
          {v.price       && <div style={{ fontSize: 17, color: TEXT_DARK, fontWeight: 600 }}>{v.price}</div>}
          {payment.length > 0 && <div style={{ fontSize: 13, color: "#7a7060" }}>{payment.join(" · ")}</div>}
        </div>
      )}
    </section>
  );
}

function SalesFitSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const fyi = Array.isArray(v.for_you_items) ? v.for_you_items : [];
  const nfyi = Array.isArray(v.not_for_you_items) ? v.not_for_you_items : [];
  const copySection = async () => {
    const lines = [v.for_you_title, ...fyi.map((x) => `✓ ${x}`), "", v.not_for_you_title, ...nfyi.map((x) => `× ${x}`)];
    try { await navigator.clipboard.writeText(lines.join("\n")); toast.success("Illeszkedés másolva"); } catch { /* noop */ }
  };
  const renderList = (title, items, setKey, prefix, kind) => {
    // kind: "for" → arany CheckCircle (#eab308) · "not" → krém/bézs Minus (#d4c896)
    const Icon = kind === "for" ? CheckCircle : Minus;
    const iconColor = kind === "for" ? GOLD_PRIMARY : "#d4c896";
    const iconWeight = kind === "for" ? "fill" : "bold";
    return (
      <div>
        <div style={{ fontSize: 17, fontWeight: 600, color: TEXT_DARK, marginBottom: 12 }}>{title}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((x, i) => (
            <div key={i} className="flex items-start" style={{ gap: 10 }}>
              <Icon size={18} weight={iconWeight} color={iconColor}
                style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <EditableInline value={x} editing={editing} multiline
                  onChange={(nv) => { const nb = items.slice(); nb[i] = nv; onChange({ ...v, [setKey]: nb }); }}
                  testid={`landing-sales-result-fit-${prefix}-${i}`}
                  style={{ fontSize: 14.5, lineHeight: 1.65, color: TEXT_BODY }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 22 }}
      data-testid="landing-sales-result-fit">
      <SectionHeader title="">
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      {renderList(v.for_you_title || "Neked szól, ha…", fyi, "for_you_items", "for", "for")}
      {renderList(v.not_for_you_title || "Nem neked szól, ha…", nfyi, "not_for_you_items", "not", "not")}
    </section>
  );
}

function SalesGuaranteeSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const copySection = async () => {
    try { await navigator.clipboard.writeText([v.title, v.highlight, v.body].filter(Boolean).join("\n\n")); toast.success("Garancia másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}
      data-testid="landing-sales-result-guarantee">
      <SectionHeader title={v.title || "Garancia"}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      {(v.highlight || v.body) && (
        <div style={{
          padding: "20px 22px", borderRadius: 12,
          background: "#fdf3d6", border: "1px solid #f0dfa8",
          display: "flex", alignItems: "flex-start", gap: 14,
        }}>
          <div style={{
            flexShrink: 0, width: 36, height: 36, borderRadius: 999,
            background: "rgba(255,219,92,0.35)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <ShieldCheck size={20} weight="fill" color={GOLD_DEEP} />
          </div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {v.highlight && (
              <EditableInline value={v.highlight} editing={editing} multiline
                onChange={(nv) => onChange({ ...v, highlight: nv })}
                testid="landing-sales-result-guarantee-highlight"
                style={{ fontSize: 16, fontWeight: 600, color: TEXT_DARK, lineHeight: 1.45 }} />
            )}
            {v.body && (
              <EditableInline value={v.body} editing={editing} multiline
                onChange={(nv) => onChange({ ...v, body: nv })}
                testid="landing-sales-result-guarantee-body"
                style={{ fontSize: 14.5, lineHeight: 1.65, color: TEXT_BODY }} />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function SalesPriceCtaSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const payment = Array.isArray(v.payment_options) ? v.payment_options : [];
  const copySection = async () => {
    try { await navigator.clipboard.writeText([v.title, v.price, payment.join(" · "), v.body, v.cta].filter(Boolean).join("\n")); toast.success("CTA másolva"); } catch { /* noop */ }
  };
  const structured = hasStructuredPricing(v.price, payment);
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}
      data-testid="landing-sales-result-pricecta">
      <SectionHeader title={v.title || ""}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      {v.body && (
        <EditableInline value={v.body} editing={editing} multiline
          onChange={(nv) => onChange({ ...v, body: nv })}
          testid="landing-sales-result-pricecta-body"
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_MID }} />
      )}
      {(v.price || payment.length > 0) && (
        <SalesPricingCards price={v.price} paymentOptions={payment} />
      )}
      {!structured && (v.price || editing) && (
        <div style={{ fontSize: 22, fontWeight: 700, color: TEXT_DARK }}>
          <EditableInline value={v.price || ""} editing={editing}
            onChange={(nv) => onChange({ ...v, price: nv })}
            testid="landing-sales-result-pricecta-price" />
        </div>
      )}
      {!structured && payment.length > 0 && (
        <div style={{ fontSize: 13.5, color: "#7a7060" }}>{payment.join(" · ")}</div>
      )}
      {(v.cta || editing) && (
        <div style={{ marginTop: 6 }}>
          <div style={{
            display: "inline-flex", padding: "14px 28px", borderRadius: 999,
            background: "linear-gradient(180deg, #ffdb5c, #eab308)",
            color: "#4a3400", fontSize: 14, fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
            boxShadow: "0 4px 12px rgba(234,179,8,0.25)",
          }}>
            <EditableInline value={v.cta || ""} editing={editing}
              onChange={(nv) => onChange({ ...v, cta: nv })}
              testid="landing-sales-result-pricecta-cta" style={{ color: "#4a3400" }} />
          </div>
        </div>
      )}
    </section>
  );
}

function SalesFaqSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const items = Array.isArray(v.items) ? v.items : [];
  const copySection = async () => {
    const lines = [v.title || "Gyakori kérdések", ""];
    items.forEach((it) => { lines.push(`Q: ${it.question}`); lines.push(`A: ${it.answer}`); lines.push(""); });
    try { await navigator.clipboard.writeText(lines.join("\n").trim()); toast.success("GYIK másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionHeader title={v.title || "Gyakori kérdések"}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {items.map((it, i) => (
          <div key={i}>
            <EditableInline value={it.question || ""} editing={editing}
              onChange={(nv) => { const nb = items.slice(); nb[i] = { ...it, question: nv }; onChange({ ...v, items: nb }); }}
              testid={`landing-sales-result-faq-q-${i}`}
              style={{ fontSize: 15.5, fontWeight: 600, color: TEXT_DARK, marginBottom: 4 }} />
            <EditableInline value={it.answer || ""} editing={editing} multiline
              onChange={(nv) => { const nb = items.slice(); nb[i] = { ...it, answer: nv }; onChange({ ...v, items: nb }); }}
              testid={`landing-sales-result-faq-a-${i}`}
              style={{ fontSize: 14.5, lineHeight: 1.6, color: TEXT_MID }} />
          </div>
        ))}
      </div>
    </section>
  );
}

function SalesClosingCtaSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const copySection = async () => {
    try { await navigator.clipboard.writeText([v.title, v.body, v.cta].filter(Boolean).join("\n")); toast.success("Záró CTA másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionHeader title={v.title || ""}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <EditableInline value={v.body || ""} editing={editing} multiline
        onChange={(nv) => onChange({ ...v, body: nv })}
        testid="landing-sales-result-closingcta-body"
        style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_MID }} />
      {(v.cta || editing) && (
        <div>
          <div style={{
            display: "inline-flex", padding: "14px 28px", borderRadius: 999,
            background: "linear-gradient(180deg, #ffdb5c, #eab308)",
            color: "#4a3400", fontSize: 14, fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
            boxShadow: "0 4px 12px rgba(234,179,8,0.25)",
          }}>
            <EditableInline value={v.cta || ""} editing={editing}
              onChange={(nv) => onChange({ ...v, cta: nv })}
              testid="landing-sales-result-closingcta-cta" style={{ color: "#4a3400" }} />
          </div>
        </div>
      )}
    </section>
  );
}

function SalesPsSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const copySection = async () => {
    try { await navigator.clipboard.writeText(`P.S. ${v.body || ""}`); toast.success("P.S. másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <SectionHeader title="P.S.">
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <EditableInline value={v.body || ""} editing={editing} multiline
        onChange={(nv) => onChange({ ...v, body: nv })}
        testid="landing-sales-result-ps-body"
        style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_MID, fontStyle: "italic" }} />
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SalesPricingCards — determinisztikus, adatvezérelt árazási kártyák.

   A komponens csak akkor renderel, ha a bemenetből (price + payment_options
   szövegek) EGYÉRTELMŰEN kinyerhető legalább egy pénzügyi mérőszám (havi
   VAGY éves). Ha mindkettő elérhető és számítható a megtakarítás
   (monthly × 12 − yearly > 0), akkor 3 kártyát mutat: Havi · Éves ·
   Megtakarítás. Egyébként a fallback jelenik meg (vagy 1–2 kártya, vagy
   a szülő szöveges renderelése).

   FONTOS: ez tisztán DETERMINISZTIKUS parser — NEM LLM. Csak a már
   generált szövegekben látott számokból dolgozik.
   ═══════════════════════════════════════════════════════════════════ */

// Havi/Éves kulcsszavak — magyar szinonimák.
const _MONTH_RX = /(\/\s*h(ó|onap)|hav(i|onta)|havi|monthly)\b/i;
const _YEAR_RX  = /(\/\s*(é|e)v|(é|e)ves|(é|e)venk(é|e)nt|(é|e)vente|annual|yearly|yr)\b/i;
const _ONETIME_RX = /(egyszeri|egyszer|one[-\s]?time|lifetime)\b/i;

// Pénznem-szótár — a legelső illeszkedő formátum győz.
const _CURRENCY_MAP = [
  { rx: /\bft\b|forint/i, symbol: "Ft" },
  { rx: /\beur\b|€/i,     symbol: "€"  },
  { rx: /\busd\b|\$/i,    symbol: "$"  },
  { rx: /\bhuf\b/i,       symbol: "Ft" },
];

function _detectCurrency(str) {
  if (!str) return "Ft";
  for (const c of _CURRENCY_MAP) if (c.rx.test(str)) return c.symbol;
  return "Ft";
}

// Kivonja az első számot egy szövegből (támogatja a "9 990", "9.990",
// "9,990" formátumokat is). Visszatér `null`-lal, ha nincs érvényes szám.
function _extractNumber(str) {
  if (!str || typeof str !== "string") return null;
  // Vedd le a HTML entitásokat és a nem breaking space-t
  const clean = str.replace(/\u00A0/g, " ").replace(/&nbsp;/g, " ");
  // Illesszen 1+ számjegyet, opcionális elválasztóval (space, ., ,) — de csak számjegyek között.
  const m = clean.match(/(\d[\d .,]*\d|\d)/);
  if (!m) return null;
  // Tisztítás: eltávolítjuk a szeparátorokat (space, pont, vessző) — a magyar formában
  // ezek ezres elválasztók, nem tizedes jelek.
  const digits = m[0].replace(/[ .,]/g, "");
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// Formázás magyar módra: 9 990 Ft. Egyéb pénznemnél: 99 €.
// Támogatja a tizedeseket is (max 2 tizedesjegy, felesleges záró nullák nélkül,
// magyar tizedesvessző). Egész értéknél nem jelenít meg tizedest.
function _formatMoney(n, currency) {
  if (!Number.isFinite(n) || n <= 0) return "";
  let str;
  if (Number.isInteger(n)) {
    str = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  } else {
    const rounded = Math.round(n * 100) / 100; // legfeljebb 2 tizedes
    const raw = rounded.toFixed(2).replace(/\.?0+$/, "");
    const [intPart, decPart] = raw.split(".");
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    str = decPart ? `${grouped},${decPart}` : grouped;
  }
  if (currency === "Ft") return `${str} Ft`;
  return `${str} ${currency}`;
}

// Adott szövegblokkokból megpróbálja kinyerni a "havi", "éves" és
// "egyszeri" numerikus árakat. Egy adott kulcshoz csak az ELSŐ érvényes
// találatot rögzítjük.
function _parsePricing(strings) {
  const currency = _detectCurrency(strings.join(" "));
  const bucket = { monthly: null, yearly: null, onetime: null, currency };
  for (const raw of strings) {
    if (!raw || typeof raw !== "string") continue;
    // Külön kezeljük a "vagy" és "/" elválasztókkal tagolt eseteket:
    // "9 990 Ft / hó vagy 89 900 Ft / év"
    const parts = raw.split(/\s+vagy\s+|\s*\|\s*|\s+·\s+/i);
    for (const part of parts) {
      const n = _extractNumber(part);
      if (!n) continue;
      if (_MONTH_RX.test(part) && bucket.monthly == null)  bucket.monthly = n;
      else if (_YEAR_RX.test(part) && bucket.yearly == null) bucket.yearly = n;
      else if (_ONETIME_RX.test(part) && bucket.onetime == null) bucket.onetime = n;
    }
  }
  return bucket;
}

// Segéd — a szülő szekció eldöntheti, hogy szükséges-e még a szöveges
// fallback renderelés (ha az árazási kártyák már megjelenítik a lényeget).
function hasStructuredPricing(price, paymentOptions) {
  const all = [price || "", ...(Array.isArray(paymentOptions) ? paymentOptions : [])];
  const b = _parsePricing(all);
  return !!(b.monthly || b.yearly || b.onetime);
}

// Közös helper — a megtakarítási jelvény szövegének determinisztikus
// kiszámítása. Ugyanezt a függvényt használja minden árazási blokk
// (Offer, PriceCta, stb.), tehát nincs duplikált számítási logika.
//
// Kimenet: `null`, ha nincs érvényes megtakarítás (0, negatív, hiányzó
// adatok, keverék pénznem). Egyébként pozitív, olvasónak szánt címke:
//   • Ha `annual_saving / monthly_price` közelít egy egész számhoz
//     (|diff| < 0.01) és >= 1 → „N hónap ajándék"
//   • Egyébként → „Évente [összeg] megtakarítás"
//
// SOHA nem tartalmaz negatív előjelet vagy veszteségnek tűnő szöveget.
function _computeSavingsBadge(monthly, yearly, currency) {
  if (!Number.isFinite(monthly) || monthly <= 0) return null;
  if (!Number.isFinite(yearly)  || yearly  <= 0) return null;
  const annualSaving = monthly * 12 - yearly;
  if (!(annualSaving > 0)) return null;
  const savedMonths = annualSaving / monthly;
  const nearest = Math.round(savedMonths);
  if (Math.abs(savedMonths - nearest) < 0.01 && nearest >= 1) {
    return `${nearest} hónap ajándék`;
  }
  return `Évente ${_formatMoney(annualSaving, currency)} megtakarítás`;
}

function SalesPricingCards({ price, paymentOptions, totalValue }) {
  const all = [price || "", ...(Array.isArray(paymentOptions) ? paymentOptions : [])];
  const b = _parsePricing(all);
  const cards = [];
  if (b.monthly) {
    cards.push({
      key: "monthly", label: "Havi",
      amount: _formatMoney(b.monthly, b.currency),
      sub: "Havi elszámolás",
    });
  }
  if (b.yearly) {
    const badge = _computeSavingsBadge(b.monthly, b.yearly, b.currency);
    cards.push({
      key: "yearly", label: "Éves",
      amount: _formatMoney(b.yearly, b.currency),
      sub: b.monthly ? `${_formatMoney(Math.round(b.yearly / 12), b.currency)}/hó` : "Éves elszámolás",
      featured: true,
      badge, // opcionális: arany „N hónap ajándék" / „Évente X megtakarítás"
    });
  }
  // Ha csak egyszeri árat találtunk (nem havi/nem éves) — 1 kártya.
  if (!b.monthly && !b.yearly && b.onetime) {
    cards.push({
      key: "onetime", label: "Egyszeri díj",
      amount: _formatMoney(b.onetime, b.currency),
      sub: totalValue ? `Teljes érték: ${totalValue}` : "Egyszeri hozzáférés",
      featured: true,
    });
  }
  if (cards.length === 0) return null;
  // Rács: 1 kártya → 1 oszlop; 2 kártya → 2 oszlop; egyéb → responsive.
  const gridCols = cards.length === 1
    ? "grid-cols-1"
    : "grid-cols-1 sm:grid-cols-2";
  return (
    <div
      data-testid="landing-sales-result-pricing-cards"
      className={`grid ${gridCols}`}
      style={{ gap: 14, marginTop: 8 }}
    >
      {cards.map((card) => {
        const featured = !!card.featured;
        const bg = featured ? "#fffdf5" : "#ffffff";
        const border = featured ? "1px solid #f0dfa8" : `1px solid ${CARD_BORDER}`;
        return (
          <div
            key={card.key}
            data-testid={`landing-sales-result-pricing-card-${card.key}`}
            style={{
              position: "relative",
              padding: card.badge ? "22px 20px 18px" : "18px 20px",
              borderRadius: 14, background: bg, border,
              display: "flex", flexDirection: "column", gap: 6,
              boxShadow: featured
                ? "0 2px 12px rgba(234,179,8,0.10)"
                : "0 1px 3px rgba(60,50,20,0.04)",
              minWidth: 0,
            }}
          >
            {card.badge && (
              <span
                data-testid={`landing-sales-result-pricing-card-${card.key}-badge`}
                style={{
                  position: "absolute",
                  top: -12, left: 16,
                  display: "inline-flex", alignItems: "center",
                  padding: "5px 12px", borderRadius: 999,
                  background: "linear-gradient(180deg, #ffdb5c, #eab308)",
                  color: "#4a3400",
                  fontFamily: "Inter, system-ui, sans-serif",
                  fontSize: 11.5, fontWeight: 600, letterSpacing: "0.01em",
                  boxShadow: "0 2px 8px rgba(234,179,8,0.30)",
                  whiteSpace: "nowrap",
                }}
              >
                {card.badge}
              </span>
            )}
            <div style={{
              fontSize: 11.5, fontWeight: 600, letterSpacing: "0.10em",
              textTransform: "uppercase", color: GOLD_LABEL,
            }}>
              {card.label}
            </div>
            <div
              className="font-heading"
              style={{
                fontSize: 26, fontWeight: 600, color: TEXT_DARK,
                lineHeight: 1.15, letterSpacing: "-0.01em", wordBreak: "break-word",
              }}
            >
              {card.amount}
            </div>
            {card.sub && (
              <div style={{ fontSize: 12.5, color: TEXT_SECOND, lineHeight: 1.45 }}>
                {card.sub}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionHeader({ title, children }) {  return (
    <header className="flex items-start justify-between flex-wrap gap-4">
      <h2 className="font-heading"
        style={{ fontSize: 19, color: TEXT_DARK, fontWeight: 500, lineHeight: 1.4, letterSpacing: "-0.01em", flex: 1, minWidth: 0 }}>
        {title}
      </h2>
      {children}
    </header>
  );
}

/* ───────── Editable inline (contentEditable-like) ───────── */
function EditableInline({ value, editing, onChange, style, multiline, testid, className }) {
  const ref = useRef(null);
  useEffect(() => {
    if (editing && ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value || "";
    }
  }, [editing, value]);
  if (!editing) {
    return <div className={className} style={style} data-testid={testid}>{value}</div>;
  }
  return (
    <div ref={ref} contentEditable suppressContentEditableWarning role="textbox"
      onBlur={(e) => onChange(e.currentTarget.innerText.replace(/\u00A0/g, " "))}
      data-testid={testid}
      className={className}
      style={{
        ...style, outline: "none",
        background: "rgba(234,179,8,0.06)",
        border: `1px dashed ${DIVIDER_START}`,
        borderRadius: 6, padding: "4px 8px",
        minHeight: multiline ? 32 : 24,
        whiteSpace: "pre-wrap",
      }}>
      {value}
    </div>
  );
}

/* ───────── Helpers ───────── */
function buildRegenPayload(sectionId, inputs, chosenTitle, page) {
  return {
    section_id: sectionId,
    chosen_title: chosenTitle || {},
    webinar_cim: inputs?.webinar_cim || "",
    mit_tanuljon: inputs?.mit_tanuljon || "",
    miert_te: inputs?.miert_te || "",
    hova_hivjuk: inputs?.hova_hivjuk || "",
    current_page: page || {},
  };
}

function pageToPlainText(page) {
  if (!page) return "";
  const p = page;
  const lines = [];
  lines.push("SALES OLDAL");
  lines.push("");

  const h = p.hero || {};
  if (h.eyebrow) lines.push(h.eyebrow.toUpperCase());
  if (h.offer_name) lines.push(h.offer_name);
  if (h.title) { lines.push(h.title); lines.push(""); }
  if (h.subtitle) { lines.push(h.subtitle); lines.push(""); }
  if (h.supporting_line) { lines.push(h.supporting_line); lines.push(""); }
  if (h.cta) { lines.push(`→ ${h.cta}`); lines.push(""); }

  const dumpText = (sec, title) => {
    if (!sec || (!sec.title && !(sec.body || []).length)) return;
    lines.push((sec.title || title).toUpperCase());
    (sec.body || []).forEach((x) => { lines.push(x); lines.push(""); });
  };
  dumpText(p.identification, "AZONOSULÁS");
  dumpText(p.beliefs, "HIEDELMEK");

  if (p.mechanism) {
    lines.push((p.mechanism.title || "MECHANIZMUS").toUpperCase());
    if (p.mechanism.highlight) { lines.push(p.mechanism.highlight); lines.push(""); }
    (p.mechanism.body || []).forEach((x) => { lines.push(x); lines.push(""); });
  }

  const dumpProof = (proof) => {
    if (!proof) return;
    lines.push((proof.title || "BIZONYÍTÉK").toUpperCase());
    (proof.items || []).forEach((it) => {
      lines.push(`„${it.quote || ""}"`);
      const meta = [it.name, it.context, it.result].filter(Boolean).join(" · ");
      if (meta) lines.push(meta);
      lines.push("");
    });
  };
  dumpProof(p.early_proof);

  const o = p.offer || {};
  if (o.title || (o.components || []).length) {
    lines.push((o.title || "AZ AJÁNLAT").toUpperCase());
    if (o.intro) { lines.push(o.intro); lines.push(""); }
    if (o.offer_name) { lines.push(o.offer_name); lines.push(""); }
    (o.components || []).forEach((c) => {
      lines.push(`• ${c.name || ""}`);
      if (c.what_it_is) lines.push(`  ${c.what_it_is}`);
      if (c.outcome)    lines.push(`  → ${c.outcome}`);
      lines.push("");
    });
    if (o.total_value) lines.push(`Összérték: ${o.total_value}`);
    if (o.price)       lines.push(`Ár: ${o.price}`);
    if ((o.payment_options || []).length) lines.push(`Fizetés: ${o.payment_options.join(" · ")}`);
    lines.push("");
  }

  const f = p.fit || {};
  if ((f.for_you_items || []).length) {
    lines.push((f.for_you_title || "Neked szól, ha…").toUpperCase());
    f.for_you_items.forEach((x) => lines.push(`✓ ${x}`));
    lines.push("");
  }
  if ((f.not_for_you_items || []).length) {
    lines.push((f.not_for_you_title || "Nem neked szól, ha…").toUpperCase());
    f.not_for_you_items.forEach((x) => lines.push(`× ${x}`));
    lines.push("");
  }

  dumpProof(p.deep_proof);

  if (p.guarantee) {
    lines.push((p.guarantee.title || "GARANCIA").toUpperCase());
    if (p.guarantee.highlight) { lines.push(p.guarantee.highlight); lines.push(""); }
    if (p.guarantee.body)      { lines.push(p.guarantee.body);      lines.push(""); }
  }

  dumpText(p.decision, "DÖNTÉSI ZÁRÁS");

  const pc = p.price_cta || {};
  if (pc.title || pc.price || pc.cta) {
    lines.push((pc.title || "ÁR ÉS CTA").toUpperCase());
    if (pc.body)  { lines.push(pc.body); lines.push(""); }
    if (pc.price) lines.push(pc.price);
    if ((pc.payment_options || []).length) lines.push(pc.payment_options.join(" · "));
    if (pc.cta)   lines.push(`→ ${pc.cta}`);
    lines.push("");
  }

  const fq = p.faq || {};
  if ((fq.items || []).length) {
    lines.push((fq.title || "GYAKORI KÉRDÉSEK").toUpperCase());
    fq.items.forEach((it) => {
      lines.push(`Q: ${it.question}`);
      if (it.answer) lines.push(`A: ${it.answer}`);
      lines.push("");
    });
  }

  const cc = p.closing_cta || {};
  if (cc.title || cc.body || cc.cta) {
    lines.push((cc.title || "ZÁRÓ CTA").toUpperCase());
    if (cc.body) { lines.push(cc.body); lines.push(""); }
    if (cc.cta)  lines.push(`→ ${cc.cta}`);
    lines.push("");
  }

  const ps = p.ps || {};
  if (ps.body) { lines.push("P.S."); lines.push(ps.body); }

  return lines.join("\n").trim() + "\n";
}
